import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { MODULE_NAMESPACES, SOURCE_MESSAGES, formatMessage, localeDirection, normaliseUiLocale, type MessageParams, type Messages, type Namespace, type UiLocale } from '@his/shared';
import { useAuth } from './auth';
import { applyDocumentLanguage, catalogue, enumValueTranslator, offeredLocale, setErrorTranslator, setModuleCatalogues, setValueTranslator, storedUiLocale, translatedOrSource } from './i18n-core';

export { applyDocumentLanguage, offeredLocale, rememberUiLocale, storedUiLocale } from './i18n-core';

interface I18nValue {
  locale: UiLocale;
  dir: 'ltr' | 'rtl';
  setPreviewLocale: (locale: string | null) => void;
}

const I18nContext = createContext<I18nValue>({ locale: 'en', dir: 'ltr', setPreviewLocale: () => undefined });

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [preview, setPreview] = useState<UiLocale | null>(() => storedUiLocale());
  const locale = offeredLocale(user?.uiLocale ?? preview ?? 'en');
  const [modulesLocale, setModulesLocale] = useState<UiLocale>('en');

  useEffect(() => {
    applyDocumentLanguage(locale);
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    if (locale === 'en') {
      setErrorTranslator(null);
      setValueTranslator(null);
      return;
    }
    void catalogue(locale, 'enum').then((target) => {
      if (cancelled) return;
      const shown = translatedOrSource(locale, 'enum', target);
      setValueTranslator(shown.locale === 'en' ? null : enumValueTranslator(shown.messages));
    });
    void catalogue(locale, 'errors').then((target) => {
      if (cancelled) return;
      const shown = translatedOrSource(locale, 'errors', target);
      setErrorTranslator(
        shown.locale === 'en' ? null : (key, params) => (shown.messages[key] ? formatMessage(shown.messages[key], params ?? {}, locale) : null),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    if (locale === 'en') {
      setModuleCatalogues('en', {});
      setModulesLocale('en');
      return;
    }
    void Promise.all(MODULE_NAMESPACES.map(async (namespace) => [namespace, translatedOrSource(locale, namespace, await catalogue(locale, namespace))] as const)).then((entries) => {
      if (cancelled) return;
      setModuleCatalogues(locale, Object.fromEntries(entries.filter(([, shown]) => shown.locale !== 'en').map(([namespace, shown]) => [namespace, shown.messages])));
      setModulesLocale(locale);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setPreviewLocale = useCallback((value: string | null) => {
    setPreview(value ? offeredLocale(value) : null);
  }, []);

  const value = useMemo<I18nValue>(() => ({ locale, dir: localeDirection(locale), setPreviewLocale }), [locale, setPreviewLocale]);
  return <I18nContext.Provider value={value}>{modulesLocale === locale ? <Fragment key={locale}>{children}</Fragment> : null}</I18nContext.Provider>;
}

export function useLanguage(): I18nValue {
  return useContext(I18nContext);
}

export interface Translator {
  t: (key: string, params?: MessageParams) => string;
  locale: UiLocale;
  translated: boolean;
}

export function useTranslation(namespace: Namespace, patientFacingLocale?: string | null): Translator {
  const { locale: screenLocale } = useLanguage();
  const locale = patientFacingLocale ? normaliseUiLocale(patientFacingLocale) : screenLocale;
  const [state, setState] = useState<{ locale: UiLocale; messages: Messages }>(() => ({ locale: 'en', messages: SOURCE_MESSAGES[namespace] }));

  useEffect(() => {
    let cancelled = false;
    if (locale === 'en') {
      setState({ locale: 'en', messages: SOURCE_MESSAGES[namespace] });
      return;
    }
    void catalogue(locale, namespace).then((target) => {
      if (!cancelled) setState(translatedOrSource(locale, namespace, target));
    });
    return () => {
      cancelled = true;
    };
  }, [locale, namespace]);

  const t = useCallback(
    (key: string, params: MessageParams = {}) => {
      const message = state.messages[key] ?? SOURCE_MESSAGES[namespace][key];
      if (message === undefined) {
        if (import.meta.env.DEV) console.warn(`Missing message ${namespace}:${key}`);
        return key;
      }
      return formatMessage(message, params, state.locale);
    },
    [state, namespace],
  );

  return { t, locale: state.locale, translated: state.locale === locale };
}
